import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi, productsApi, customersApi } from '../services/api';

export default function NewOrder() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    customer_address: '', customer_city: '', customer_zip: '',
    delivery_method: 'post', notes: '',
  });
  const [items, setItems] = useState([{ product_id: '', product_name: '', variant: '', quantity: 1, price_unit: 0 }]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    productsApi.list({ limit: 200 }).then(r => setProducts(r.data));
    customersApi.list({ limit: 200 }).then(r => setCustomers(r.data));
  }, []);

  function selectProduct(idx, productId) {
    const p = products.find(x => x.id === parseInt(productId));
    setItems(items.map((item, i) => i === idx ? { ...item, product_id: productId, product_name: p?.name || '', price_unit: p?.price_sell || 0 } : item));
  }

  function selectCustomer(customerId) {
    const c = customers.find(x => x.id === parseInt(customerId));
    if (c) setForm(f => ({ ...f, customer_name: c.name, customer_email: c.email, customer_phone: c.phone, customer_address: c.address, customer_city: c.city, customer_zip: c.zip, customer_id: c.id }));
  }

  function addItem() {
    setItems([...items, { product_id: '', product_name: '', variant: '', quantity: 1, price_unit: 0 }]);
  }

  function removeItem(idx) {
    setItems(items.filter((_, i) => i !== idx));
  }

  const subtotal = items.reduce((s, i) => s + i.price_unit * i.quantity, 0);
  const shipping = form.delivery_method === 'pickup' ? 0 : subtotal >= 1500 ? 0 : 89;
  const total = subtotal + shipping;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!items.some(i => i.product_name)) return setError('Přidejte alespoň jednu položku');
    setSaving(true);
    try {
      const result = await ordersApi.create({ ...form, items: items.filter(i => i.product_name) });
      navigate(`/orders/${result.id}`);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Nová objednávka</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Customer */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>👤 Zákazník</div>
            <div className="form-group">
              <label className="form-label">Existující zákazník</label>
              <select className="form-control" onChange={e => selectCustomer(e.target.value)}>
                <option value="">— Nový zákazník —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email || c.phone})</option>)}
              </select>
            </div>
            {['customer_name', 'customer_email', 'customer_phone', 'customer_address', 'customer_city', 'customer_zip'].map(field => (
              <div className="form-group" key={field}>
                <label className="form-label">{{ customer_name:'Jméno', customer_email:'Email', customer_phone:'Telefon', customer_address:'Adresa', customer_city:'Město', customer_zip:'PSČ' }[field]}</label>
                <input className="form-control" value={form[field]} onChange={e => setForm(f => ({...f, [field]: e.target.value}))} required={field === 'customer_name'} />
              </div>
            ))}
          </div>

          {/* Delivery */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>🚚 Doručení & Poznámka</div>
            <div className="form-group">
              <label className="form-label">Způsob doručení</label>
              <select className="form-control" value={form.delivery_method} onChange={e => setForm(f => ({...f, delivery_method: e.target.value}))}>
                <option value="post">📮 Česká pošta</option>
                <option value="pickup">🏠 Vyzvednutí</option>
                <option value="personal">🤝 Osobní odběr</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Poznámka</label>
              <textarea className="form-control" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={5} />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">🛒 Položky</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}>+ Přidat</button>
          </div>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 100px auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
              <div>
                <label className="form-label">Produkt</label>
                <select className="form-control" value={item.product_id} onChange={e => selectProduct(idx, e.target.value)}>
                  <option value="">— vyberte nebo napište —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.price_sell} Kč)</option>)}
                </select>
                {!item.product_id && <input className="form-control" style={{ marginTop: 4 }} placeholder="Nebo zadejte ručně" value={item.product_name} onChange={e => setItems(items.map((x, i) => i === idx ? {...x, product_name: e.target.value} : x))} />}
              </div>
              <div>
                <label className="form-label">Varianta</label>
                <input className="form-control" value={item.variant} onChange={e => setItems(items.map((x, i) => i === idx ? {...x, variant: e.target.value} : x))} />
              </div>
              <div>
                <label className="form-label">Ks</label>
                <input className="form-control" type="number" min="1" value={item.quantity} onChange={e => setItems(items.map((x, i) => i === idx ? {...x, quantity: parseInt(e.target.value)||1} : x))} />
              </div>
              <div>
                <label className="form-label">Cena/ks</label>
                <input className="form-control" type="number" step="0.01" value={item.price_unit} onChange={e => setItems(items.map((x, i) => i === idx ? {...x, price_unit: parseFloat(e.target.value)||0} : x))} />
              </div>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(idx)} style={{ marginTop: 20 }}>✕</button>
            </div>
          ))}
          <div style={{ borderTop: '2px solid var(--border)', paddingTop: 12, marginTop: 8, textAlign: 'right', fontSize: 15 }}>
            <div>Mezisoučet: <strong>{subtotal.toFixed(2)} Kč</strong></div>
            <div>Poštovné: <strong>{shipping} Kč</strong></div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6 }}>CELKEM: {total.toFixed(2)} Kč</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/orders')}>Zrušit</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Ukládám...' : 'Vytvořit objednávku'}</button>
        </div>
      </form>
    </div>
  );
}
